// ============================================================================
// DESIGN TOKENS — the single source of truth for the Cable CRM design system.
// Everything visual (colors, spacing, radius, shadow, type) derives from here.
// Consume via the MUI theme (theme.js) as `theme.palette.*`, `theme.spacing()`,
// etc. — do NOT hardcode hex/px in components.
// ============================================================================

// ── Brand ──────────────────────────────────────────────────────────────────
// Deep navy-purple shell gradient + amber accent (the product's identity).
export const brand = {
  purpleDeep: "#0f0c29", // sidebar/navbar gradient start
  purple: "#302b63", // brand primary
  purpleSoft: "#4a4381", // hover/hairlines on dark
  amber: "#fbbd2c", // accent / CTA / active
  amberDark: "#e6a913", // amber hover
  onAmber: "#1a1a2e", // text on amber (dark for contrast)
};

// Shell (sidebar + top bar) gradient — used by the dark chrome only.
export const shellGradient = `linear-gradient(180deg, ${brand.purpleDeep}, ${brand.purple})`;
export const shellGradientH = `linear-gradient(90deg, ${brand.purpleDeep}, ${brand.purple})`;

// ── Neutrals / surfaces (dark purple content area) ──────────────────────────
// The content area is the BRAND PURPLE itself (#302b63) — the same colour the
// sidebar/navbar gradient resolves to — so the whole app is one continuous
// dark surface instead of dark chrome wrapped around a light page. Cards and
// tables are lifted ABOVE that ground (lighter, not darker), which is what
// gives depth on a dark UI; hairlines are white at low alpha so they read on
// every one of those levels without a second border colour per surface.
export const neutral = {
  bg: "#302b63", // app content background — brand purple
  surface: "#393372", // cards / tables / modals — one step up from the ground
  surfaceAlt: "#443d88", // table header / toolbar band — two steps up
  surfaceTint: "#3e3878", // row hover / subtle fill
  border: "rgba(255,255,255,0.15)", // hairlines, dividers, input borders
  borderStrong: "rgba(255,255,255,0.30)",
};

// ── Accents (KPI tiles / chart series) ───────────────────────────────────────
// Deliberately the LIGHT end of each hue: a mid-tone accent (indigo-600, say)
// disappears against a dark purple ground. Used at full strength for the
// icon/figure and at low alpha for the tile ground — see StatCard.
export const accent = {
  indigo: "#818cf8",
  amber: brand.amber,
  emerald: "#34d399",
  sky: "#38bdf8",
  violet: "#c084fc",
  rose: "#fb7185",
};

// ── Text ─────────────────────────────────────────────────────────────────────
// Everything sits on the dark ground now, so "primary" is white and the old
// near-black is gone. `heading` is the brand amber: page titles, section
// headings, table column headers and dialog titles all take it, which is what
// separates a heading from body copy here instead of weight alone.
export const text = {
  primary: "#ffffff",
  secondary: "rgba(255,255,255,0.72)",
  disabled: "rgba(255,255,255,0.45)",
  heading: brand.amber, // 7.5:1 on #302b63 — AAA
  onDark: "#ffffff",
  onDarkMuted: "rgba(255,255,255,0.64)",
};

// ── Status (semantic) ────────────────────────────────────────────────────────
// Light-end variants for the same reason as `accent`; the *Bg pairs are now
// translucent washes rather than pale solids, so they tint whatever surface
// they land on instead of punching a light hole in it.
export const status = {
  success: "#4ade80",
  successBg: "rgba(74,222,128,0.18)",
  warning: "#fb923c",
  warningBg: "rgba(251,146,60,0.18)",
  error: "#fb7185",
  errorBg: "rgba(251,113,133,0.18)",
  info: "#7dd3fc",
  infoBg: "rgba(125,211,252,0.18)",
};

// ── Spacing (4/8 rhythm) — MUI base is 8px; these are the named steps ────────
export const spacing = {
  base: 8,
  page: { xs: 2, sm: 3, lg: 4 }, // 16 / 24 / 32 px page padding (theme.spacing units)
  section: 3, // 24px between page sections
  card: { xs: 2, sm: 3 }, // 16 / 24 px inside cards
  field: 2.5, // 20px between form fields
};

// ── Radius ────────────────────────────────────────────────────────────────────
export const radius = {
  sm: 8, // inputs, buttons, chips
  md: 12, // cards, tables
  lg: 16, // dialogs, drawers
  pill: 999,
};

// ── Elevation ─────────────────────────────────────────────────────────────────
// Darker and deeper than the light-theme values: a 6%-black shadow is simply
// invisible against a dark purple ground, so depth comes from a stronger,
// near-black cast (plus the lighter surface colours in `neutral`).
export const shadow = {
  card: "0 1px 3px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.22)",
  raised: "0 8px 24px rgba(0,0,0,0.38)",
  overlay: "0 20px 56px rgba(0,0,0,0.52)",
};

// ── Type scale (px). Body 14 for data density; inputs bumped to 16 on mobile
//    to avoid iOS focus auto-zoom (see theme component overrides). ─────────────
export const fontFamily =
  '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const typeScale = {
  h1: { size: 32, weight: 700, lh: 1.2 },
  h2: { size: 26, weight: 700, lh: 1.25 },
  h3: { size: 22, weight: 700, lh: 1.3 },
  h4: { size: 20, weight: 700, lh: 1.3 },
  h5: { size: 18, weight: 700, lh: 1.35 },
  h6: { size: 16, weight: 700, lh: 1.4 },
  subtitle: { size: 15, weight: 600, lh: 1.5 },
  body1: { size: 14, weight: 400, lh: 1.6 },
  body2: { size: 13, weight: 400, lh: 1.55 },
  caption: { size: 12, weight: 500, lh: 1.5 },
  button: { size: 14, weight: 600, lh: 1.5 },
};

// Breakpoints (px) — systematic, matches MUI defaults we rely on.
export const breakpoints = { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 };

export default { brand, shellGradient, shellGradientH, neutral, accent, text, status, spacing, radius, shadow, fontFamily, typeScale, breakpoints };
